/**
 * Picking up a potje that was started on another phone.
 *
 * The scorekeeper is offline-first: a potje lives on the device it was started
 * on and is pushed to the server as a whole document. That is fine until the
 * evening ends on the iPad and the next day only your own phone is there — the
 * scores exist, but not where you are.
 *
 * So: ask the server which unfinished potjes it has, and claim one. Claiming is a
 * handover, not a copy. Ownership moves to this device and the previous one stops
 * syncing that potje, because two phones pushing the same document would take
 * turns overwriting each other's rounds.
 */

import type { ScoreGame } from '@shared/types/game';
import type { KlaverjasScoreGame } from '@shared/types/klaverjas';
import { api } from '@/lib/api';
import { useScorekeeper } from '@/store/scorekeeper';
import { useKlaverjasScore } from '@/store/klaverjasScore';

export interface ResumableGame {
  id: string;
  /** 'boerenbridge' or 'klaverjas', from the column that means which game. */
  game: string;
  updatedAt: string;
  players: string[];
}

export function fetchResumable(): Promise<ResumableGame[]> {
  return api<ResumableGame[]>('/api/games/resumable');
}

/**
 * Claim a potje and put it in the right store.
 *
 * Returns where to go, so the caller can navigate — the two games have different
 * screens and this is the one place that knows which document belongs to which.
 */
export async function claimGame(id: string): Promise<string> {
  const { document } = await api<{ document: unknown }>(`/api/games/${id}/claim`, {
    method: 'POST',
  });

  /*
   * The document is whatever the client that owned it last sent. It is shaped by
   * the schema the server validated on the way in, so this is a narrowing rather
   * than a trust decision — but it is still checked, because a row written by an
   * older version is exactly the kind of thing that shows up as a blank screen.
   */
  if (!isScoreDocument(document)) {
    throw new Error('Dit potje is niet leesbaar op deze versie.');
  }

  // `game: 'klaverjas'` is the discriminant the server's own schema uses, and it
  // is the only field that tells the two documents apart — boerenbridge leaves it
  // out entirely on games synced from before klaverjassen existed.
  if (isKlaverjas(document)) {
    useKlaverjasScore.getState().adoptGame(document);
    return `/klaverjas/${document.id}`;
  }

  useScorekeeper.getState().adoptGame(document);
  return `/score/${document.id}`;
}

function isKlaverjas(game: ScoreGame | KlaverjasScoreGame): game is KlaverjasScoreGame {
  return 'game' in game && game.game === 'klaverjas';
}

function isScoreDocument(value: unknown): value is ScoreGame | KlaverjasScoreGame {
  if (typeof value !== 'object' || value === null) return false;
  const game = value as Partial<ScoreGame & KlaverjasScoreGame>;
  return (
    typeof game.id === 'string' &&
    Array.isArray(game.seats) &&
    (Array.isArray(game.rounds) || Array.isArray(game.deals))
  );
}
